import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  CheckCircle2, XCircle, Loader2, AlertTriangle, Building2, Phone, Mail,
  ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtZar = (n) =>
  `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------------------
// PublicQuotePage — at /q/:token, no login required. Renders a branded
// client-facing view with Accept/Decline buttons. Once accepted/declined,
// the page locks into a confirmation state.
// ---------------------------------------------------------------------------
const PublicQuotePage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null); // 'accept' | 'decline' | null
  const [signature, setSignature] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/quotes/${token}`);
        if (cancelled) return;
        setQuote(res.data);
        document.title = `Quotation · ${res.data.title || 'Brainwave'}`;
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.status === 404 ? 'not_found' : 'unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    if (!action) return;
    if (action === 'accept' && !signature.trim()) {
      toast.error('Please type your name as a signature before accepting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/public/quotes/${token}/respond`,
        { action, signature: signature.trim() || null, message: message.trim() || null },
      );
      setQuote(res.data);
      setAction(null);
      toast.success(
        action === 'accept' ? 'Thank you — your acceptance was recorded.' : 'Response recorded.'
      );
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error('This quote has already been responded to.');
        const refresh = await axios.get(`${BACKEND_URL}/api/public/quotes/${token}`);
        setQuote(refresh.data);
      } else {
        toast.error(err.response?.data?.detail || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#00CED1' }} />
      </div>
    );
  }
  if (error === 'not_found') {
    return <NotFound />;
  }
  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="text-center text-gray-600 text-sm">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
          Could not load quote. Please try again later.
        </div>
      </div>
    );
  }

  const isFinal = quote.status === 'accepted' || quote.status === 'rejected';
  const brand = quote.brand || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-quote-page">
      {/* Brand header */}
      <header className="text-white px-6 py-5" style={{ backgroundColor: '#00CED1' }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-80">Quotation</p>
            <h1 className="text-xl sm:text-2xl font-bold mt-0.5" data-testid="public-quote-brand">
              {brand.name || 'Brainwave Architectural Projects'}
            </h1>
          </div>
          <div className="text-xs sm:text-sm text-right opacity-90 leading-snug">
            <p data-testid="public-quote-brand-phone">{brand.phone}</p>
            <p data-testid="public-quote-brand-email">{brand.email}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Title + status */}
        <section className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">
                Quote #{quote.id?.slice(0, 8).toUpperCase()}
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mt-1" data-testid="public-quote-title">
                {quote.title}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {quote.trade_label} · {(quote.created_at || '').slice(0, 10)}
              </p>
            </div>
            <StatusBadge status={quote.status} />
          </div>

          {quote.client_name && (
            <p className="mt-4 text-sm text-gray-700" data-testid="public-quote-client">
              Prepared for <strong>{quote.client_name}</strong>
              {quote.client_email ? <span className="text-gray-500"> · {quote.client_email}</span> : null}
            </p>
          )}
        </section>

        {/* Materials */}
        {quote.material_lines?.length > 0 && (
          <Section title="Materials" testid="public-quote-materials">
            <LinesTable
              headers={['Description', 'Qty', 'Unit', 'Unit price', 'Line total']}
              rows={quote.material_lines.map((l) => [
                l.name,
                String(l.qty),
                l.unit,
                fmtZar(l.unit_price_zar),
                fmtZar(l.line_total ?? Number(l.qty) * Number(l.unit_price_zar)),
              ])}
            />
          </Section>
        )}

        {/* Labor */}
        {quote.labor_lines?.length > 0 && (
          <Section title="Labor" testid="public-quote-labor">
            <LinesTable
              headers={['Role', 'Hours', 'Hourly rate', 'Line total']}
              rows={quote.labor_lines.map((l) => [
                l.role,
                String(l.hours),
                fmtZar(l.hourly_rate_zar),
                fmtZar(l.line_total ?? Number(l.hours) * Number(l.hourly_rate_zar)),
              ])}
            />
          </Section>
        )}

        {/* Totals */}
        <section className="bg-white rounded-lg shadow-sm p-6" data-testid="public-quote-totals">
          <div className="max-w-sm ml-auto space-y-1 text-sm">
            <Row label="Materials subtotal" value={fmtZar(quote.materials_total)} />
            <Row label="Labor subtotal" value={fmtZar(quote.labor_total)} />
            <Row label="Subtotal" value={fmtZar(quote.subtotal)} />
            <Row label={`VAT (${(quote.vat_rate * 100).toFixed(0)}%)`} value={fmtZar(quote.vat_amount)} />
            <div className="border-t pt-2 mt-2">
              <div
                className="flex items-baseline justify-between p-3 rounded-md text-white"
                style={{ backgroundColor: '#00CED1' }}
              >
                <span className="font-bold uppercase text-xs tracking-wide">Total incl. VAT</span>
                <span className="text-xl font-bold font-mono" data-testid="public-quote-total">
                  {fmtZar(quote.total)}
                </span>
              </div>
            </div>
          </div>
        </section>

        {quote.notes && (
          <Section title="Notes" testid="public-quote-notes">
            <div className="bg-white rounded-lg shadow-sm p-5 text-sm text-gray-700 whitespace-pre-line">
              {quote.notes}
            </div>
          </Section>
        )}

        {/* Decision panel */}
        {!isFinal && (
          <section
            className="bg-white rounded-lg shadow-sm p-6 border-2"
            style={{ borderColor: '#A5F3F2' }}
            data-testid="public-quote-decision"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5" style={{ color: '#00CED1' }} />
              <h3 className="font-semibold text-gray-900">Ready to proceed?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This quotation is valid for 30 days. Click <strong>Accept</strong> below to confirm
              and we'll be in touch to schedule the work. Click <strong>Decline</strong> if it
              doesn't suit — you can leave a note if you'd like changes.
            </p>

            {!action && (
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => setAction('accept')}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold flex-1 min-w-[140px]"
                  data-testid="public-quote-accept-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Accept quote
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setAction('decline')}
                  className="font-semibold flex-1 min-w-[140px] border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="public-quote-decline-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" /> Decline
                </Button>
              </div>
            )}

            {action && (
              <div className="space-y-3 mt-2" data-testid="public-quote-response-form">
                {action === 'accept' && (
                  <div>
                    <Label className="text-xs">Type your full name as a signature *</Label>
                    <Input
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="e.g. John Smith"
                      autoFocus
                      data-testid="public-quote-signature"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-xs">
                    {action === 'accept' ? 'Optional note' : 'Reason / what changes would suit?'}
                  </Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder={
                      action === 'accept'
                        ? 'Any special instructions or scheduling preferences…'
                        : 'Let us know what would make this work for you…'
                    }
                    data-testid="public-quote-response-message"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setAction(null); setSignature(''); setMessage(''); }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={`text-white font-semibold ${action === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-800'}`}
                    data-testid="public-quote-response-submit"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : action === 'accept' ? (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Confirm {action === 'accept' ? 'acceptance' : 'decline'}
                  </Button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Final confirmation state */}
        {isFinal && (
          <section
            className={`rounded-lg shadow-sm p-6 border-2 ${
              quote.status === 'accepted'
                ? 'bg-green-50 border-green-300'
                : 'bg-gray-50 border-gray-300'
            }`}
            data-testid="public-quote-final"
          >
            <div className="flex items-start gap-3">
              {quote.status === 'accepted' ? (
                <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-8 w-8 text-gray-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h3 className="font-bold text-lg">
                  {quote.status === 'accepted' ? 'Quote accepted — thank you!' : 'Quote declined'}
                </h3>
                <p className="text-sm text-gray-700 mt-1">
                  {quote.status === 'accepted'
                    ? `We'll be in touch shortly to schedule the work. A confirmation has been sent to ${brand.email}.`
                    : `Your response has been recorded. If you'd like a revised quote, just email ${brand.email} or call ${brand.phone}.`}
                </p>
                {quote.client_response?.signature && (
                  <p className="text-xs text-gray-500 mt-2">
                    Signed by <strong>{quote.client_response.signature}</strong> on{' '}
                    {(quote.client_response.responded_at || '').replace('T', ' ').slice(0, 19)}
                  </p>
                )}
                {quote.client_response?.message && (
                  <div className="text-xs bg-white rounded p-3 mt-3 border border-gray-200 whitespace-pre-line">
                    <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1">Your note</p>
                    {quote.client_response.message}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-gray-500 pt-2 pb-8">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{brand.name}</span>
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{brand.phone}</span>
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{brand.email}</span>
          </div>
          <p className="mt-2">All prices in ZAR · VAT included · This quotation is valid for 30 days</p>
        </footer>
      </main>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    draft: { bg: '#F3F4F6', text: '#525252', label: 'Awaiting your decision' },
    sent: { bg: '#ECFEFF', text: '#0891b2', label: 'Awaiting your decision' },
    accepted: { bg: '#DCFCE7', text: '#16a34a', label: 'Accepted' },
    rejected: { bg: '#F3F4F6', text: '#525252', label: 'Declined' },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className="text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
      style={{ color: s.text, backgroundColor: s.bg }}
      data-testid="public-quote-status-badge"
    >
      {s.label}
    </span>
  );
};

const Section = ({ title, children, testid }) => (
  <section data-testid={testid}>
    <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wide mb-2">{title}</h3>
    {children}
  </section>
);

const LinesTable = ({ headers, rows }) => (
  <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className={`px-4 py-2 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-t">
            {r.map((c, ci) => (
              <td
                key={ci}
                className={`px-4 py-2 ${ci === 0 ? '' : 'text-right font-mono'} ${ci === r.length - 1 ? 'font-semibold' : ''}`}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-gray-600">{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
    <div className="max-w-md text-center">
      <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
      <h1 className="text-xl font-bold text-gray-900">Quote not found</h1>
      <p className="text-sm text-gray-600 mt-2">
        The link you followed is invalid or has expired. Please check the URL or contact the sender for a fresh link.
      </p>
    </div>
  </div>
);

export default PublicQuotePage;
